import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Info, KeyRound } from 'lucide-react';
import { toast } from 'react-toastify';
import { Action, Chip, EmptyState, ErrorNote, Field, Input, Loading, Modal, Panel, PanelHead } from '../aa';
import type { ApiKey, CreatedApiKey } from '../../services/sdkKeysApi';
import { aiRuntimeApi, runtimeBaseUrl, type RuntimeModels } from '../../services/aiRuntimeApi';

/*
 * AI Runtime: the company's own OpenAI-compatible API. The Owner makes keys
 * here, their apps send requests with them, Jev picks the model, and every
 * call is paid from the company's prepaid balance.
 */

const errorText = (err: unknown, fallback: string) => {
  const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return fallback;
};

const day = (iso: string | null) => {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const price = (usd: number) => `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

const copy = async (text: string, what: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error('Copy failed. Select the text and copy it by hand.');
  }
};

const MONO = 'bg-transparent p-0 font-code text-ink';

const CopyField: React.FC<{ value: string; what: string }> = ({ value, what }) => (
  <div className="flex h-11 items-center gap-2 rounded-act border border-line-strong bg-canvas-inset pl-3.5 pr-1">
    <code className={`min-w-0 flex-1 truncate text-[13px] ${MONO}`} title={value}>{value}</code>
    <button type="button" aria-label={`Copy ${what}`} title={`Copy ${what}`} onClick={() => copy(value, what)}
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-act text-ink-muted transition-colors hover:bg-white hover:text-ink focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ink/15">
      <Copy size={16} />
    </button>
  </div>
);

const NewKeyModal: React.FC<{ onClose: () => void; onCreated: (key: CreatedApiKey) => void }> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = name.trim().length > 0;

  const create = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      onCreated(await aiRuntimeApi.createKey(name.trim()));
    } catch (err) {
      toast.error(errorText(err, 'Could not create the key.'));
      setBusy(false);
    }
  };

  return (
    <Modal size="dialog" onClose={onClose} context="AI Runtime"
      footer={(
        <div className="flex justify-end gap-2">
          <Action variant="outline" className="min-w-[96px]" onClick={onClose}>Cancel</Action>
          <Action className="min-w-[112px]" onClick={create} disabled={busy || !valid} reason="Give the key a name first.">Create key</Action>
        </div>
      )}>
      <h2 className="m-0 text-[20px] font-bold leading-[27px] text-ink">New API key</h2>
      <p className="mb-5 mt-1.5 text-[13px] leading-[21.5px] text-ink-dim">
        Name it after the app that will use it, so you know which key to revoke later.
      </p>
      <Field label="Key name" htmlFor="ai-runtime-key-name">
        <Input id="ai-runtime-key-name" value={name} maxLength={128} placeholder="Support chatbot"
          onChange={(e) => setName(e.target.value)} disabled={busy} autoFocus />
      </Field>
    </Modal>
  );
};

const ShowKeyModal: React.FC<{ created: CreatedApiKey; onClose: () => void }> = ({ created, onClose }) => (
  <Modal size="dialog" onClose={onClose} context="AI Runtime"
    footer={<div className="flex justify-end"><Action className="min-w-[136px]" onClick={onClose}>I saved the key</Action></div>}>
    <h2 className="m-0 text-[20px] font-bold leading-[27px] text-ink">Copy your key now</h2>
    <p className="mb-5 mt-1.5 text-[13px] leading-[21.5px] text-ink-dim">
      This is the only time the full key for "{created.name}" is shown. Store it somewhere safe. If you lose it, rotate the key.
    </p>
    <CopyField value={created.key} what="Key" />
  </Modal>
);

const ConfirmModal: React.FC<{
  title: string; body: string; action: string; danger?: boolean; onConfirm: () => Promise<void>; onClose: () => void;
}> = ({ title, body, action, danger = false, onConfirm, onClose }) => {
  const [busy, setBusy] = useState(false);
  let variant: 'primary' | 'danger' = 'primary';
  if (danger) variant = 'danger';

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal size="dialog" onClose={onClose} context="AI Runtime"
      footer={(
        <div className="flex justify-end gap-2">
          <Action variant="outline" className="min-w-[96px]" onClick={onClose}>Cancel</Action>
          <Action variant={variant} className="min-w-[112px]" onClick={confirm} disabled={busy} reason="Working on it">{action}</Action>
        </div>
      )}>
      <h2 className="m-0 text-[20px] font-bold leading-[27px] text-ink">{title}</h2>
      <p className="mb-0 mt-1.5 text-[13px] leading-[21.5px] text-ink-dim">{body}</p>
    </Modal>
  );
};

const KeyRow: React.FC<{ apiKey: ApiKey; onRotate: () => void; onRevoke: () => void }> = ({ apiKey, onRotate, onRevoke }) => (
  <li className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-act border border-line-soft bg-white px-4 py-3.5">
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-act bg-canvas-inset text-ink-muted" aria-hidden="true">
      <KeyRound size={16} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="truncate text-[14px] font-semibold text-ink">{apiKey.name}</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[12px] text-ink-dim">
        <code className={`text-[12px] ${MONO} text-ink-muted`}>{apiKey.key_prefix}…</code>
        <span aria-hidden="true">·</span>
        <span>Created {day(apiKey.created_at)}</span>
        <span aria-hidden="true">·</span>
        <span>Last used {day(apiKey.last_used_at)}</span>
      </div>
    </div>
    <div className="flex shrink-0 gap-2">
      <Action variant="outline" className="!h-9 min-w-[88px]" onClick={onRotate}>Rotate</Action>
      <Action variant="outline" className="!h-9 min-w-[88px] !text-danger hover:!bg-danger-soft" onClick={onRevoke}>Revoke</Action>
    </div>
  </li>
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ModelsPanel: React.FC<{ models: RuntimeModels | null; failed: boolean }> = ({ models, failed }) => {
  if (failed) return <Panel><div className="p-6"><ErrorNote message="Could not load the model list." /></div></Panel>;
  if (!models) return <Panel><Loading label="Loading models" /></Panel>;
  return (
    <Panel>
      <PanelHead kicker="Models" title="What your keys can call"
        desc="Send model as jev/auto and Jev picks the best model for each request. You can also name a model below. Prices are per million tokens." />
      {!models.available && (
        <div className="px-6 pb-6"><EmptyState title="Models are not available right now" reason="Try again in a minute." /></div>
      )}
      {models.available && (
        <ul className="m-0 list-none divide-y divide-line-soft border-t border-line-soft px-6 py-1">
          <li className="flex min-h-[48px] flex-wrap items-center justify-between gap-2 py-2.5">
            <div className="flex items-center gap-2">
              <code className={`text-[13px] font-semibold ${MONO}`}>{models.auto}</code>
              <Chip tone="ok">Recommended</Chip>
            </div>
            <span className="text-[12px] text-ink-dim">Billed at the price of the model Jev picks</span>
          </li>
          {models.models.map((m) => (
            <li key={m.id} className="flex min-h-[48px] items-center justify-between gap-4 py-2.5">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <code className={`shrink-0 text-[13px] ${MONO}`}>{m.id}</code>
                <span className="min-w-0 truncate text-[12px] text-ink-dim" title={m.label}>{m.label}</span>
                {m.vision && <Chip>Images</Chip>}
              </div>
              <span className="shrink-0 whitespace-nowrap text-[12px] tabular-nums text-ink-dim">
                {price(m.inputPerMillionUsd)} in · {price(m.outputPerMillionUsd)} out
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

type Pending = { kind: 'rotate' | 'revoke'; key: ApiKey };

const infoTone = (open: boolean) => {
  if (open) return 'bg-canvas-inset text-ink';
  return 'text-ink-muted';
};

export const AiRuntimePanel: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [keysError, setKeysError] = useState('');
  // const [models, setModels] = useState<RuntimeModels | null>(null);
  // const [modelsFailed, setModelsFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [showExample, setShowExample] = useState(false);
  const baseUrl = runtimeBaseUrl();

  const loadKeys = useCallback(async () => {
    try {
      setKeys(await aiRuntimeApi.listKeys());
      setKeysError('');
    } catch (err) {
      setKeysError(errorText(err, 'Could not load your keys.'));
    }
  }, []);

  useEffect(() => {
    loadKeys();
    // aiRuntimeApi.models().then(setModels).catch(() => setModelsFailed(true));
  }, [loadKeys]);

  const shown = (key: CreatedApiKey) => {
    setCreating(false);
    setPending(null);
    setCreated(key);
    loadKeys();
  };

  const confirmPending = async () => {
    if (!pending) return;
    try {
      if (pending.kind === 'rotate') {
        shown(await aiRuntimeApi.rotateKey(pending.key.id));
        return;
      }
      await aiRuntimeApi.revokeKey(pending.key.id);
      toast.success(`${pending.key.name} revoked`);
      setPending(null);
      loadKeys();
    } catch (err) {
      toast.error(errorText(err, 'That did not work. Try again.'));
    }
  };

  const example = [
    `curl ${baseUrl}/chat/completions \\`,
    '  -H "Authorization: Bearer YOUR_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '{"model": "jev/auto", "messages": [{"role": "user", "content": "Hello"}]}'`,
  ].join('\n');

  return (
    <div className="mx-auto max-w-[760px] space-y-6">
      <Panel>
        <PanelHead kicker="AI Runtime" title="Connect your apps to our AI"
          desc="Your apps send requests here like they would to OpenAI. We pick the best model for each one, and every call is paid from your prepaid balance."
          action={<Action className="min-w-[112px]" onClick={() => setCreating(true)}>New key</Action>} />
        <div className="space-y-5 px-6 pb-6">
          <Field label="Base URL" hint={(
            <span className="inline-flex items-center gap-1">
              Works with any OpenAI-compatible SDK: set this as the base URL and use your key.
              <button type="button" aria-label="Show an example request" aria-expanded={showExample} aria-controls="ai-runtime-example"
                title="Show an example request" onClick={() => setShowExample((v) => !v)}
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-canvas-inset hover:text-ink focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ink/15 ${infoTone(showExample)}`}>
                <Info size={15} />
              </button>
            </span>
          )}>
            <CopyField value={baseUrl} what="Base URL" />
          </Field>
          {showExample && (
            <div id="ai-runtime-example">
              <div className="mb-1.5 text-[13px] font-semibold text-ink">Example request</div>
              <pre className="m-0 overflow-x-auto rounded-act bg-ink px-5 py-4 font-code text-[12.5px] leading-[20px] text-white">{example}</pre>
            </div>
          )}
        </div>
        <div className="border-t border-line-soft bg-canvas px-6 py-5">
          {keysError && <ErrorNote message={keysError} />}
          {!keysError && !keys && <Loading label="Loading keys" />}
          {keys && keys.length === 0 && (
            <EmptyState title="No keys yet" reason="Create a key for each app you connect. You can rotate or revoke it any time." />
          )}
          {keys && keys.length > 0 && (
            <ul className="m-0 list-none space-y-2 p-0">
              {keys.map((k) => (
                <KeyRow key={k.id} apiKey={k}
                  onRotate={() => setPending({ kind: 'rotate', key: k })}
                  onRevoke={() => setPending({ kind: 'revoke', key: k })} />
              ))}
            </ul>
          )}
        </div>
      </Panel>
      {/* <ModelsPanel models={models} failed={modelsFailed} /> */}
      {creating && <NewKeyModal onClose={() => setCreating(false)} onCreated={shown} />}
      {created && <ShowKeyModal created={created} onClose={() => setCreated(null)} />}
      {pending && pending.kind === 'rotate' && (
        <ConfirmModal title={`Rotate ${pending.key.name}?`} action="Rotate key"
          body="You get a new key right away and the old one stops working. Update your app with the new key."
          onConfirm={confirmPending} onClose={() => setPending(null)} />
      )}
      {pending && pending.kind === 'revoke' && (
        <ConfirmModal title={`Revoke ${pending.key.name}?`} action="Revoke key" danger
          body="Apps using this key stop working right away. This cannot be undone."
          onConfirm={confirmPending} onClose={() => setPending(null)} />
      )}
    </div>
  );
};

export default AiRuntimePanel;
